import type { Platform, PlatformScope } from '@/lib/performance/types';
import { formatMoney } from '@/lib/performance/money';
import { formatResultsSplit } from '@/lib/performance/results';
import { summariseScope, type MetricsByPlatform, type PlatformMetrics } from '@/lib/performance/kpis';
import { PLATFORM_LABEL, PlatformLogo } from '@/lib/performance/platform';

export interface KpiSummaryProps {
  metrics: MetricsByPlatform;
  scope: PlatformScope;
  currencySymbol: string;
}

/**
 * The four Overview KPI tiles, ordered Spend / Impressions / Clicks / Conversions.
 * Spend, Impressions and Clicks show a summed headline plus a per-platform
 * breakdown under the "All" scope; Conversions is rendered split (Google
 * conversions vs Meta leads) and is never merged into one number (#5971).
 */
export function KpiSummary({ metrics, scope, currencySymbol }: KpiSummaryProps) {
  const summary = summariseScope(metrics, scope);
  const showSplit = summary.platforms.length > 1;

  const num = (n: number) => Math.round(n).toLocaleString();
  const perPlatform = (fmt: (m: PlatformMetrics) => string): { platform: Platform; value: string }[] =>
    showSplit ? summary.platforms.map((p) => ({ platform: p, value: fmt(metrics[p]!) })) : [];

  const tiles: { label: string; value: string; lines: { platform: Platform; value: string }[] }[] = [
    {
      label: 'Spend',
      value: formatMoney(summary.spend, currencySymbol),
      lines: perPlatform((m) => formatMoney(m.spend, currencySymbol)),
    },
    { label: 'Impressions', value: num(summary.impressions), lines: perPlatform((m) => num(m.impressions)) },
    { label: 'Clicks', value: num(summary.clicks), lines: perPlatform((m) => num(m.clicks)) },
    { label: 'Conversions', value: formatResultsSplit(summary.results), lines: [] },
  ];

  // Every tile is zero: label the €0/0 grid as an intentional empty state so it
  // doesn't read as a failed load (#4372).
  const allZero = summary.spend === 0 && summary.impressions === 0 && summary.clicks === 0;

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {tiles.map((tile) => (
          <div key={tile.label} className="rounded-lg border bg-white p-4 shadow-sm">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{tile.label}</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">{tile.value}</p>
            {tile.lines.length > 0 && (
              <div className="mt-2 space-y-0.5">
                {tile.lines.map((line) => (
                  <p key={line.platform} className="flex items-center gap-1.5 text-xs text-gray-500">
                    <PlatformLogo platform={line.platform} className="h-3 w-3 shrink-0" />
                    <span>
                      {PLATFORM_LABEL[line.platform]} {line.value}
                    </span>
                  </p>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      {allZero && (
        <p className="text-xs text-gray-400">No spend, clicks, or impressions were recorded in the selected period.</p>
      )}
    </div>
  );
}
